import { formatUnits } from '@lifi/sdk'
import { useAccount } from '@lifi/wallet-management'
import { type JSX, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { useApplyAmount } from '../../hooks/useApplyAmount.js'
import { useAvailableChains } from '../../hooks/useAvailableChains.js'
import { useMaxSendAmount } from '../../hooks/useMaxSendAmount.js'
import { useTokenAddressBalance } from '../../hooks/useTokenAddressBalance.js'
import { useWidgetConfig } from '../../providers/WidgetProvider/WidgetProvider.js'
import type { FormTypeProps } from '../../stores/form/types.js'
import { FormKeyHelper } from '../../stores/form/types.js'
import { useFieldValues } from '../../stores/form/useFieldValues.js'
import type { DisabledUIConfig } from '../../types/widget.js'
import { Chip, ChipContainer } from './PercentageChips.style.js'

export const PercentageChips: React.NamedExoticComponent<FormTypeProps> = memo(
  ({ formType }: FormTypeProps): JSX.Element | null => {
    const { t } = useTranslation()
    const { getChainById } = useAvailableChains()
    const { disabledUI } = useWidgetConfig()
    const applyAmount = useApplyAmount(formType)

    const amountKey = FormKeyHelper.getAmountKey(formType)
    const isDisabled = !!disabledUI?.[amountKey as keyof DisabledUIConfig]

    const [chainId, tokenAddress] = useFieldValues(
      FormKeyHelper.getChainKey(formType),
      FormKeyHelper.getTokenKey(formType)
    )

    const chain = getChainById(chainId)
    const { account } = useAccount({ chainType: chain?.chainType })

    const { token } = useTokenAddressBalance(chainId, tokenAddress)
    const maxAmount = useMaxSendAmount(chainId, tokenAddress)

    const handlePercentage = (percentage: number): void => {
      if (maxAmount && token?.decimals) {
        const percentageAmount = (maxAmount * BigInt(percentage)) / 100n
        applyAmount(formatUnits(percentageAmount, token.decimals))
      }
    }

    const handleMax = (): void => {
      if (maxAmount && token?.decimals) {
        applyAmount(formatUnits(maxAmount, token.decimals))
      }
    }

    if (formType !== 'from' || isDisabled || !token || !account?.isConnected) {
      return null
    }

    return (
      <ChipContainer>
        <Chip data-delay="0" onClick={() => handlePercentage(25)}>
          25%
        </Chip>
        <Chip data-delay="1" onClick={() => handlePercentage(50)}>
          50%
        </Chip>
        <Chip data-delay="2" onClick={() => handlePercentage(75)}>
          75%
        </Chip>
        <Chip data-delay="3" onClick={handleMax}>
          {t('button.max')}
        </Chip>
      </ChipContainer>
    )
  }
)
